import { create } from "zustand";

const API_BASE = "https://my.lapse.kz";
const DEFAULT_AVATAR = "";

const readJson = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const persistAuth = (token, user) => {
  if (token) {
    localStorage.setItem("auth_token", token);
  } else {
    localStorage.removeItem("auth_token");
  }

  if (user) {
    localStorage.setItem("user_data", JSON.stringify(user));
  } else {
    localStorage.removeItem("user_data");
  }
};

export const formatGestureName = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed) return "";
  return `${trimmed.charAt(0).toUpperCase()}${trimmed.slice(1).toLowerCase()}`;
};

const SEARCH_MATCH_THRESHOLD = 0.8;

const normalizeComparableText = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/ё/g, "е");
};

const uniqueNonEmpty = (values) => {
  return Array.from(new Set((values || []).map((value) => String(value || "").trim()).filter(Boolean)));
};

const buildYoSearchVariants = (query) => {
  const base = formatGestureName(query);
  if (!base) return [];

  const yoToYe = base.replace(/ё/g, "е").replace(/Ё/g, "Е");
  const yeToYo = base.replace(/е/g, "ё").replace(/Е/g, "Ё");
  return uniqueNonEmpty([base, yoToYe, yeToYo]);
};

const levenshteinDistance = (left, right) => {
  if (left === right) return 0;
  if (!left.length) return right.length;
  if (!right.length) return left.length;

  const prev = Array(right.length + 1).fill(0);
  const curr = Array(right.length + 1).fill(0);

  for (let j = 0; j <= right.length; j += 1) {
    prev[j] = j;
  }

  for (let i = 1; i <= left.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= right.length; j += 1) {
      const cost = left[i - 1] === right[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost
      );
    }
    for (let j = 0; j <= right.length; j += 1) {
      prev[j] = curr[j];
    }
  }

  return prev[right.length];
};

const getStringSimilarity = (left, right) => {
  const safeLeft = normalizeComparableText(left);
  const safeRight = normalizeComparableText(right);

  if (!safeLeft || !safeRight) return 0;
  if (safeLeft === safeRight) return 1;

  const maxLen = Math.max(safeLeft.length, safeRight.length);
  if (!maxLen) return 0;

  const distance = levenshteinDistance(safeLeft, safeRight);
  return Math.max(0, 1 - distance / maxLen);
};

const getBestSimilarityToVariants = (value, variants) => {
  let best = 0;
  for (const variant of variants || []) {
    best = Math.max(best, getStringSimilarity(value, variant));
  }
  return best;
};

const normalizeAssociationSignsRelation = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => flattenEntity(item) || item).filter(Boolean);
  }
  if (Array.isArray(value?.data)) {
    return value.data.map((item) => flattenEntity(item) || item).filter(Boolean);
  }
  return [];
};

const normalizeSignRequest = (item) => {
  if (!item) return null;

  const envelopeId = item.id;
  const envelopeDocumentId = item.documentId;
  const attrs = item.attributes ? item.attributes : null;
  const source = attrs ? { ...attrs } : { ...item };

  const id = envelopeId ?? source.id;
  const documentId = envelopeDocumentId ?? source.documentId ?? null;

  const relationUser = source.user?.data?.attributes
    ? { id: source.user?.data?.id, ...source.user.data.attributes }
    : source.user;

  const rawRating = source.rating;
  const rating =
    rawRating != null && rawRating !== "" && Number.isFinite(Number(rawRating))
      ? Number(rawRating)
      : null;

  return {
    id,
    documentId,
    lemma: source.lemma || "",
    added: !!source.added,
    notes: source.notes || "",
    rating,
    publishedAt: source.publishedAt ?? null,
    user: relationUser || null,
  };
};

export const PLACEHOLDER_PREFIX = "p:";

export const getEntityDocumentId = (entity) => {
  const raw = entity?.documentId ?? entity?.document_id;
  if (raw == null || raw === "") return null;
  return String(raw).trim();
};

export const getRequestDocumentId = (request) => getEntityDocumentId(request);

/** @deprecated use getRequestDocumentId */
export const getRequestPlaceholderId = getRequestDocumentId;

export const buildRequestPlaceholder = (requestOrDocumentId) => {
  const documentId = typeof requestOrDocumentId === "object"
    ? getRequestDocumentId(requestOrDocumentId)
    : String(requestOrDocumentId ?? "").trim();

  if (!documentId) return null;
  return `${PLACEHOLDER_PREFIX}${documentId}`;
};

export const buildSignDocumentEntry = (sign) => getEntityDocumentId(sign);

export const buildRequestSearchTokens = (request) => {
  const documentId = getRequestDocumentId(request);
  const tokens = [];

  if (documentId) {
    tokens.push(buildRequestPlaceholder(request));
    tokens.push(documentId);
  }

  const legacyNumericId = Number(request?.id);
  if (Number.isFinite(legacyNumericId)) {
    tokens.push(legacyNumericId);
    tokens.push(`${PLACEHOLDER_PREFIX}${legacyNumericId}`);
  }

  return Array.from(new Set(tokens.filter((token) => token != null && token !== "")));
};

export const buildSignSearchTokens = (sign) => {
  const documentId = getEntityDocumentId(sign);
  const tokens = [];

  if (documentId) tokens.push(documentId);

  const legacyNumericId = Number(sign?.id);
  if (Number.isFinite(legacyNumericId)) tokens.push(legacyNumericId);

  return Array.from(new Set(tokens.filter((token) => token != null && token !== "")));
};

export const migrateSignEntryToDocumentId = (entry, { signIdToDocumentId, requestIdToDocumentId }) => {
  if (entry == null || entry === "") return entry;

  if (typeof entry === "string") {
    const trimmed = entry.trim();
    if (!trimmed) return entry;

    const placeholderMatch = trimmed.match(/^p:(.+)$/i);
    if (placeholderMatch) {
      const payload = placeholderMatch[1];
      if (/^\d+$/.test(payload)) {
        const mapped = requestIdToDocumentId?.get(Number(payload));
        if (mapped) return `${PLACEHOLDER_PREFIX}${mapped}`;
      }
      return `${PLACEHOLDER_PREFIX}${payload}`;
    }

    if (/^\d+$/.test(trimmed)) {
      const mapped = signIdToDocumentId?.get(Number(trimmed));
      if (mapped) return mapped;
      return entry;
    }

    return trimmed;
  }

  if (typeof entry === "number" && Number.isFinite(entry)) {
    const mapped = signIdToDocumentId?.get(entry);
    return mapped || entry;
  }

  return entry;
};

export const migrateSignEntriesToDocumentIds = (signs, maps) => {
  if (!Array.isArray(signs)) return [];
  return signs.map((entry) => migrateSignEntryToDocumentId(entry, maps));
};

const parseTranslationDataField = (value) => {
  if (value == null || value === "") return {};

  let parsed = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return {};
    }
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {};
  }

  if (parsed.data && typeof parsed.data === "object" && !Array.isArray(parsed.data)) {
    const inner = parsed.data;
    if (Array.isArray(inner.signs) || inner.signs != null) {
      return inner;
    }
  }

  return parsed;
};

const extractTranslationSigns = (dataField) => {
  const rawData = parseTranslationDataField(dataField);

  if (Array.isArray(rawData.signs)) {
    return [...rawData.signs];
  }

  if (typeof rawData.signs === "string") {
    try {
      const parsed = JSON.parse(rawData.signs);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // ignore invalid JSON in signs
    }
  }

  if (rawData.signs && typeof rawData.signs === "object") {
    return Object.values(rawData.signs).filter((value) => value != null && value !== "");
  }

  return [];
};

export const normalizeSignEntryToken = (entry) => {
  if (entry == null) return null;

  if (typeof entry === "number" && Number.isFinite(entry)) {
    return entry;
  }

  if (typeof entry === "string") {
    const trimmed = entry.trim();
    if (!trimmed) return null;

    const placeholderMatch = trimmed.match(/^p:(.+)$/i);
    if (placeholderMatch) {
      return `${PLACEHOLDER_PREFIX}${placeholderMatch[1]}`;
    }

    if (/^\d+$/.test(trimmed)) {
      return Number(trimmed);
    }

    return trimmed;
  }

  if (typeof entry === "object") {
    if (entry.documentId != null || entry.document_id != null) {
      return getEntityDocumentId(entry);
    }
    if (entry.value != null) {
      return normalizeSignEntryToken(entry.value);
    }
    if (entry.requestDocumentId != null || entry.request_document_id != null) {
      return buildRequestPlaceholder(entry.requestDocumentId ?? entry.request_document_id);
    }
    if (entry.request_id != null || entry.requestId != null) {
      return buildRequestPlaceholder(entry.request_id ?? entry.requestId);
    }
    if (entry.id != null && (entry.type === "placeholder" || entry.kind === "placeholder")) {
      return buildRequestPlaceholder(entry);
    }
  }

  return String(entry);
};

export const signEntryMatchesToken = (entry, token) => {
  const entryToken = normalizeSignEntryToken(entry);
  const searchToken = normalizeSignEntryToken(token);

  if (entryToken == null || searchToken == null) return false;

  if (typeof entryToken === "number" && typeof searchToken === "number") {
    return entryToken === searchToken;
  }

  return String(entryToken) === String(searchToken);
};

export const getSignEntryDisplayValue = (entry) => {
  const token = normalizeSignEntryToken(entry);
  if (token == null) return "—";
  return String(token);
};

const normalizeTranslation = (item) => {
  if (!item) return null;

  const source = item.attributes ? { id: item.id, ...item.attributes } : item;
  const rawDataSource = source.data;
  const rawData = parseTranslationDataField(rawDataSource);
  const signs = extractTranslationSigns(rawDataSource);
  const relationSentence = source.sentence?.data?.attributes
    ? {
      id: source.sentence?.data?.id,
      documentId: source.sentence?.data?.documentId || null,
      ...source.sentence.data.attributes,
    }
    : (source.sentence?.data
      ? { id: source.sentence.data.id, documentId: source.sentence.data.documentId || null, ...source.sentence.data }
      : source.sentence);

  return {
    id: source.id,
    documentId: source.documentId || null,
    data: { ...rawData, signs },
    language: source.language || "",
    sentence: relationSentence || null,
    __rawData: typeof rawDataSource === "string" ? rawDataSource : null,
  };
};

const translationCacheKey = (translation) => String(translation?.documentId || translation?.id || "");

const mergeTranslationRecords = (targetMap, translation, sentenceFallback = null) => {
  const normalized = normalizeTranslation(translation);
  if (!normalized) return;

  if (!normalized.sentence && sentenceFallback) {
    normalized.sentence = sentenceFallback;
  }

  const key = translationCacheKey(normalized);
  if (!key) return;

  const existing = targetMap.get(key);
  if (!existing) {
    targetMap.set(key, normalized);
    return;
  }

  if (!existing.sentence && normalized.sentence) {
    existing.sentence = normalized.sentence;
  }

  const existingSigns = existing?.data?.signs;
  const nextSigns = normalized?.data?.signs;
  const existingHasSigns = Array.isArray(existingSigns) && existingSigns.length > 0;
  const nextHasSigns = Array.isArray(nextSigns) && nextSigns.length > 0;

  if (!existingHasSigns && nextHasSigns) {
    existing.data = normalized.data;
  }

  if (!existing.__rawData && normalized.__rawData) {
    existing.__rawData = normalized.__rawData;
  }
};

const fetchPaginatedApiItems = async ({
  safeBase,
  headers,
  endpoints,
  pageSize,
  maxPages,
  buildParams,
}) => {
  for (const endpoint of endpoints) {
    for (const includePreview of [true, false]) {
      try {
        const collected = [];
        let page = 1;

        while (page <= maxPages) {
          const params = buildParams({ page, pageSize, includePreview });
          const resp = await fetch(`${safeBase}${endpoint}?${params.toString()}`, { headers });

          if (!resp.ok) {
            let message = "Ошибка загрузки данных";
            try {
              const errData = await resp.json();
              message = errData?.error?.message || errData?.message || message;
            } catch {
              const text = await resp.text();
              if (text) message = text;
            }
            const error = new Error(message);
            error.status = resp.status;
            throw error;
          }

          const payload = await resp.json();
          const items = Array.isArray(payload) ? payload : (payload?.data || []);
          collected.push(...items);

          if (!items.length || items.length < pageSize) break;
          page += 1;
        }

        return collected;
      } catch (error) {
        if (includePreview && error?.status === 400) {
          continue;
        }
      }
    }
  }

  return [];
};

const fetchAllSignRequestRawItems = async (base, headers) => {
  const pageSize = 100;
  const maxPages = 500;
  const safeBase = base.replace(/\/$/, "");
  const publicationModes = [
    { status: "published" },
    { publicationState: "live" },
    {},
  ];

  for (const mode of publicationModes) {
    const collected = [];
    let page = 1;

    try {
      while (page <= maxPages) {
        const params = new URLSearchParams();
        params.append("populate", "user");
        params.append("pagination[page]", String(page));
        params.append("pagination[pageSize]", String(pageSize));
        if (mode.status) params.append("status", mode.status);
        if (mode.publicationState) params.append("publicationState", mode.publicationState);

        const resp = await fetch(`${safeBase}/api/sign-requests?${params.toString()}`, { headers });
        if (!resp.ok) {
          let message = "Ошибка загрузки заявок";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }

        const payload = await resp.json();
        const items = Array.isArray(payload) ? payload : (payload?.data || []);
        collected.push(...items);

        if (!items.length || items.length < pageSize) break;
        page += 1;
      }

      if (collected.length) return collected;
    } catch {
      // try next mode
    }
  }

  return [];
};

const fetchAllTranslationsFromApi = async (base, token) => {
  const pageSize = 100;
  const maxPages = 500;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const safeBase = base.replace(/\/$/, "");
  const byKey = new Map();

  const buildTranslationParams = ({ page, pageSize: size, includePreview }, populateValue) => {
    const params = new URLSearchParams();
    params.append("pagination[page]", String(page));
    params.append("pagination[pageSize]", String(size));
    if (includePreview) {
      params.append("publicationState", "preview");
    }
    if (populateValue) {
      params.append("populate", populateValue);
    }
    return params;
  };

  const buildSentenceParams = ({ page, pageSize: size, includePreview }) => {
    const params = new URLSearchParams();
    params.append("pagination[page]", String(page));
    params.append("pagination[pageSize]", String(size));
    params.append("populate", "*");
    if (includePreview) {
      params.append("publicationState", "preview");
    }
    return params;
  };

  const directWithoutPopulate = await fetchPaginatedApiItems({
    safeBase,
    headers,
    endpoints: ["/api/translations", "/translations"],
    pageSize,
    maxPages,
    buildParams: (ctx) => buildTranslationParams(ctx, ""),
  });
  directWithoutPopulate.forEach((item) => mergeTranslationRecords(byKey, item));

  const directWithSentence = await fetchPaginatedApiItems({
    safeBase,
    headers,
    endpoints: ["/api/translations", "/translations"],
    pageSize,
    maxPages,
    buildParams: (ctx) => buildTranslationParams(ctx, "sentence"),
  });
  directWithSentence.forEach((item) => mergeTranslationRecords(byKey, item));

  const sentenceItems = await fetchPaginatedApiItems({
    safeBase,
    headers,
    endpoints: ["/api/sentences", "/sentences"],
    pageSize,
    maxPages,
    buildParams: buildSentenceParams,
  });

  for (const sentenceItem of sentenceItems) {
    const sentence = normalizeSentence(sentenceItem);
    if (!sentence) continue;

    const sentenceInfo = {
      id: sentence.id,
      documentId: sentence.documentId || null,
      text: sentence.text || "",
      language: sentence.language || "",
    };

    for (const translation of sentence.translations || []) {
      mergeTranslationRecords(byKey, translation, sentenceInfo);
    }
  }

  const result = Array.from(byKey.values());
  if (!result.length) {
    throw new Error("Не удалось загрузить переводы");
  }

  return result;
};

const translationContainsSignToken = (translation, token) => {
  if (token == null || token === "") return false;

  const signs = translation?.data?.signs;
  if (Array.isArray(signs) && signs.some((entry) => signEntryMatchesToken(entry, token))) {
    return true;
  }

  const searchToken = normalizeSignEntryToken(token);
  if (searchToken == null) return false;

  const haystack = translation?.__rawData
    || (typeof translation?.data === "string" ? translation.data : JSON.stringify(translation?.data || {}));

  return String(haystack).includes(String(searchToken));
};

const translationContainsRequest = (translation, request) => {
  return buildRequestSearchTokens(request).some((token) => translationContainsSignToken(translation, token));
};

const mapTranslationUsageItems = (translations) => {
  return translations.map((translation) => ({
    translationId: translation.id,
    translationDocumentId: translation.documentId || null,
    language: translation.language || "",
    signs: Array.isArray(translation?.data?.signs) ? [...translation.data.signs] : [],
    sentence: translation.sentence
      ? {
        id: translation.sentence.id ?? null,
        documentId: translation.sentence.documentId || null,
        text: translation.sentence.text || "",
        language: translation.sentence.language || "",
      }
      : null,
  }));
};

const replaceSignTokenInList = (signs, fromToken, toValue) => {
  if (!Array.isArray(signs)) return [];

  const fromTokens = Array.isArray(fromToken) ? fromToken : [fromToken];
  const activeTokens = fromTokens.filter((token) => token != null && token !== "");
  if (!activeTokens.length) return [...signs];

  return signs.map((entry) => (
    activeTokens.some((token) => signEntryMatchesToken(entry, token)) ? toValue : entry
  ));
};

const normalizeSentence = (item) => {
  if (!item) return null;

  const source = item.attributes ? { id: item.id, ...item.attributes } : item;

  return {
    id: source.id,
    documentId: source.documentId || null,
    text: source.text || "",
    language: source.language || "",
    view: source.view ?? null,
    translations: Array.isArray(source?.translations?.data)
      ? source.translations.data.map((relation) => (relation?.attributes ? { id: relation.id, ...relation.attributes } : relation)).filter(Boolean)
      : (Array.isArray(source?.translations) ? source.translations.filter(Boolean) : []),
    createdAt: source.createdAt || null,
    updatedAt: source.updatedAt || null,
    publishedAt: source.publishedAt || null,
  };
};

const flattenEntity = (item) => {
  if (!item) return null;

  const source = item.attributes ? { id: item.id, ...item.attributes } : item;

  const normalizeRelationList = (relationValue) => {
    if (Array.isArray(relationValue)) {
      return relationValue.map(flattenEntity).filter(Boolean);
    }

    if (Array.isArray(relationValue?.data)) {
      return relationValue.data.map(flattenEntity).filter(Boolean);
    }

    return relationValue;
  };

  return {
    ...source,
    sign_videos: normalizeRelationList(source.sign_videos),
    word_associations: normalizeRelationList(source.word_associations),
    signs: normalizeRelationList(source.signs),
    words: normalizeRelationList(source.words),
  };
};

export const useAppStore = create((set, get) => ({
  API: API_BASE,
  activePage: "home",
  visualPreset: localStorage.getItem("visual_preset") || "glass-default",
  token: localStorage.getItem("auth_token") || null,
  user: readJson("user_data"),
  isAuthenticated: !!(localStorage.getItem("auth_token") && readJson("user_data")),
  authLoading: false,
  authChecking: false,
  authError: "",
  signRequests: [],
  signRequestsLoading: false,
  signRequestsError: "",
  signRequestsMutationLoading: false,
  signRequestsMutationError: "",
  signRequestUsageLoading: false,
  signRequestUsageError: "",
  signUsageLoading: false,
  signUsageError: "",
  dashboardStats: {
    loading: false,
    error: "",
    requests: {
      total: 0,
      new: 0,
      processed: 0,
      avgRating: null,
      topByRating: [],
    },
    signs: { total: 0, withoutVideo: 0 },
    sentences: { total: 0, untranslated: 0, translated: 0 },
  },
  sentences: [],
  sentencesLoading: false,
  sentencesError: "",
  sentencesMutationLoading: false,
  sentencesMutationError: "",
  sentencesCreateLoading: false,
  sentencesCreateError: "",
  sentencesCreateReport: null,
  translationCreateLoading: false,
  translationCreateError: "",
  sentencesPagination: {
    page: 1,
    pageSize: 25,
    pageCount: 1,
    total: 0,
  },
  signs: [],
  signsLoading: false,
  signsError: "",
  signsQuery: "",
  createSignLoading: false,
  createSignError: "",
  deleteSignLoading: false,
  deleteSignError: "",
  signVideosById: {},
  signVideosLoading: false,
  signVideosError: "",
  signVideoUploadLoading: false,
  signVideoUploadError: "",
  wordAssociationsById: {},
  wordAssociationsLoading: false,
  wordAssociationsError: "",
  associationSignsSearchResults: [],
  associationSignsSearchLoading: false,
  associationSignsSearchError: "",
  associationSignsMutationLoading: false,
  associationSignsMutationError: "",
  associationWordsSearchResults: [],
  associationWordsSearchLoading: false,
  associationWordsSearchError: "",
  associationWordsMutationLoading: false,
  associationWordsMutationError: "",
  wordMutationLoading: false,
  wordMutationError: "",
  signsPagination: {
    page: 1,
    pageSize: 6,
    pageCount: 1,
    total: 0,
  },

  setActivePage: (page) => set({ activePage: page }),
  setVisualPreset: (preset) => {
    const next = preset === "glass-cold" ? "glass-cold" : "glass-default";
    localStorage.setItem("visual_preset", next);
    set({ visualPreset: next });
  },
  toggleVisualPreset: () => {
    const current = get().visualPreset;
    const next = current === "glass-cold" ? "glass-default" : "glass-cold";
    localStorage.setItem("visual_preset", next);
    set({ visualPreset: next });
  },

  setAuthError: (message) => set({ authError: message || "" }),
  clearAuthError: () => set({ authError: "" }),

  logout: () => {
    persistAuth(null, null);
    set({
      token: null,
      user: null,
      isAuthenticated: false,
      authError: "",
      activePage: "home",
      signRequests: [],
      signRequestsError: "",
      signRequestsMutationError: "",
      sentences: [],
      sentencesError: "",
      sentencesMutationError: "",
      sentencesCreateError: "",
      sentencesCreateReport: null,
      translationCreateLoading: false,
      translationCreateError: "",
      sentencesPagination: {
        page: 1,
        pageSize: 25,
        pageCount: 1,
        total: 0,
      },
      signs: [],
      signsError: "",
      signsQuery: "",
      createSignError: "",
      deleteSignError: "",
      signVideosById: {},
      signVideosError: "",
      signVideoUploadError: "",
      wordAssociationsById: {},
      wordAssociationsError: "",
      associationSignsSearchResults: [],
      associationSignsSearchError: "",
      associationSignsMutationError: "",
      associationWordsSearchResults: [],
      associationWordsSearchError: "",
      associationWordsMutationError: "",
      wordMutationError: "",
      signsPagination: {
        page: 1,
        pageSize: 6,
        pageCount: 1,
        total: 0,
      },
    });
  },

  fetchSentences: async (page = 1, pageSize = 25, options = {}) => {
    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const safePage = Number.isFinite(Number(page)) && Number(page) > 0 ? Number(page) : 1;
    const safePageSize = Number.isFinite(Number(pageSize)) && Number(pageSize) > 0 ? Number(pageSize) : 25;
    const safeSort = typeof options?.sort === "string" && options.sort.trim() ? options.sort.trim() : "createdAt:desc";
    const translationFilter = options?.translationFilter === "translated" || options?.translationFilter === "untranslated"
      ? options.translationFilter
      : "all";

    set({ sentencesLoading: true, sentencesError: "" });

    const fetchPage = async (endpoint) => {
      const params = new URLSearchParams();
      params.append("pagination[page]", String(safePage));
      params.append("pagination[pageSize]", String(safePageSize));
      params.append("populate", "*");
      params.append("sort", safeSort);
      if (translationFilter === "untranslated") {
        params.append("filters[translations][$null]", "true");
      } else if (translationFilter === "translated") {
        params.append("filters[translations][$notNull]", "true");
      }
      const resp = await fetch(`${base}${endpoint}?${params.toString()}`, { headers });

      if (!resp.ok) {
        let message = "Ошибка загрузки предложений";
        try {
          const errData = await resp.json();
          message = errData?.error?.message || errData?.message || message;
        } catch {
          const text = await resp.text();
          if (text) message = text;
        }
        throw new Error(message);
      }

      return resp.json();
    };

    try {
      let payload;
      try {
        payload = await fetchPage("/api/sentences");
      } catch {
        payload = await fetchPage("/sentences");
      }

      const items = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalized = items.map(normalizeSentence).filter(Boolean);
      const meta = payload?.meta?.pagination || {};

      set({
        sentences: normalized,
        sentencesError: "",
        sentencesPagination: {
          page: meta.page || safePage,
          pageSize: meta.pageSize || safePageSize,
          pageCount: meta.pageCount || 1,
          total: meta.total || normalized.length,
        },
      });

      return normalized;
    } catch (error) {
      set({
        sentences: [],
        sentencesError: error.message || "Ошибка загрузки предложений",
        sentencesPagination: {
          page: safePage,
          pageSize: safePageSize,
          pageCount: 1,
          total: 0,
        },
      });
      return [];
    } finally {
      set({ sentencesLoading: false });
    }
  },

  updateSentence: async (sentence, fields) => {
    const sentenceId = sentence?.id;
    const sentenceDocumentId = sentence?.documentId;

    if (!sentenceId && !sentenceDocumentId) {
      set({ sentencesMutationError: "Не найден ID предложения" });
      return false;
    }

    const safeText = String(fields?.text || "").trim();
    const safeLanguage = String(fields?.language || "").trim();
    const safeView = fields?.view === "" ? null : (fields?.view ?? null);

    if (!safeText) {
      set({ sentencesMutationError: "Поле text не может быть пустым" });
      return false;
    }

    if (!safeLanguage) {
      set({ sentencesMutationError: "Поле language не может быть пустым" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    if (!token) {
      set({ sentencesMutationError: "Нет токена" });
      return false;
    }

    set({ sentencesMutationLoading: true, sentencesMutationError: "" });

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      const payload = {
        data: {
          text: safeText,
          language: safeLanguage,
          view: safeView,
        },
      };
      const keys = Array.from(new Set([sentenceDocumentId, sentenceId].filter(Boolean)));
      let lastError = null;

      for (const key of keys) {
        for (const endpoint of [`/api/sentences/${key}`, `/sentences/${key}`]) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              let message = "Ошибка обновления предложения";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || errData?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            const result = await resp.json();
            const normalizedResult = normalizeSentence(result?.data || result);

            set((state) => ({
              sentences: state.sentences.map((item) => {
                const sameDocument = sentenceDocumentId && item?.documentId && String(item.documentId) === String(sentenceDocumentId);
                const sameId = sentenceId && item?.id && Number(item.id) === Number(sentenceId);
                if (!sameDocument && !sameId) return item;
                return normalizedResult || { ...item, ...payload.data };
              }),
              sentencesMutationError: "",
            }));
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка обновления предложения");
    } catch (error) {
      set({ sentencesMutationError: error.message || "Ошибка обновления предложения" });
      return false;
    } finally {
      set({ sentencesMutationLoading: false });
    }
  },

  createSentence: async (fields) => {
    const safeText = String(fields?.text || "").trim();
    const safeLanguage = String(fields?.language || "").trim();
    const safeView = fields?.view === "" ? null : (fields?.view ?? null);

    if (!safeText) {
      set({ sentencesCreateError: "Поле text не может быть пустым" });
      return null;
    }

    if (!safeLanguage) {
      set({ sentencesCreateError: "Поле language не может быть пустым" });
      return null;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    if (!token) {
      set({ sentencesCreateError: "Нет токена" });
      return null;
    }

    set({ sentencesCreateLoading: true, sentencesCreateError: "", sentencesCreateReport: null });

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };
      const payload = {
        data: {
          text: safeText,
          language: safeLanguage,
          view: safeView,
        },
      };

      const postTo = async (endpoint) => {
        const resp = await fetch(base + endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          let message = "Ошибка создания предложения";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || errData?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }

        return resp.json();
      };

      let result;
      try {
        result = await postTo("/api/sentences");
      } catch {
        result = await postTo("/sentences");
      }

      set({ sentencesCreateError: "", sentencesCreateReport: { created: 1, failed: 0 } });
      return normalizeSentence(result?.data || result);
    } catch (error) {
      set({ sentencesCreateError: error.message || "Ошибка создания предложения", sentencesCreateReport: null });
      return null;
    } finally {
      set({ sentencesCreateLoading: false });
    }
  },

  createSentencesBulk: async (items = []) => {
    const preparedItems = (items || [])
      .map((item) => ({
        text: String(item?.text || "").trim(),
        language: String(item?.language || "").trim(),
        view: item?.view === "" ? null : (item?.view ?? null),
      }))
      .filter((item) => item.text && item.language);

    if (!preparedItems.length) {
      set({ sentencesCreateError: "Нет валидных предложений для создания", sentencesCreateReport: null });
      return { created: [], failed: [] };
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    if (!token) {
      set({ sentencesCreateError: "Нет токена", sentencesCreateReport: null });
      return { created: [], failed: preparedItems };
    }

    set({ sentencesCreateLoading: true, sentencesCreateError: "", sentencesCreateReport: null });

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const createByPayload = async (item) => {
      const payload = {
        data: {
          text: item.text,
          language: item.language,
          view: item.view,
        },
      };

      const postTo = async (endpoint) => {
        const resp = await fetch(base + endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          let message = "Ошибка создания предложения";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || errData?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }

        return resp.json();
      };

      try {
        return await postTo("/api/sentences");
      } catch {
        return postTo("/sentences");
      }
    };

    try {
      const created = [];
      const failed = [];

      for (const item of preparedItems) {
        try {
          const result = await createByPayload(item);
          const normalized = normalizeSentence(result?.data || result);
          if (normalized) created.push(normalized);
        } catch (error) {
          failed.push({
            ...item,
            error: error?.message || "Ошибка создания предложения",
          });
        }
      }

      const report = { created: created.length, failed: failed.length };

      set({
        sentencesCreateError: failed.length ? `Не удалось создать: ${failed.length}` : "",
        sentencesCreateReport: report,
      });

      return { created, failed };
    } finally {
      set({ sentencesCreateLoading: false });
    }
  },

  createTranslation: async ({
    sentence,
    signDocumentIdsOrdered = [],
    signIdsOrdered = [],
    language,
    notes = "",
    reviewed = false,
  }) => {
    const sentenceId = sentence?.id;
    const sentenceDocumentId = sentence?.documentId;
    if (!sentenceId && !sentenceDocumentId) {
      set({ translationCreateError: "Выберите предложение (sentence)" });
      return null;
    }

    const rawOrdered = signDocumentIdsOrdered?.length ? signDocumentIdsOrdered : signIdsOrdered;
    const ordered = (rawOrdered || [])
      .map((value) => String(value ?? "").trim())
      .filter(Boolean);
    if (!ordered.length) {
      set({ translationCreateError: "Добавьте хотя бы один жест в последовательность" });
      return null;
    }

    const safeLang = String(language || "").trim();
    if (!safeLang) {
      set({ translationCreateError: "Укажите language (например, как в перечислении Strapi)" });
      return null;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const userId = get().user?.id;
    if (!token) {
      set({ translationCreateError: "Нет токена" });
      return null;
    }
    if (!userId) {
      set({ translationCreateError: "Не найден id пользователя" });
      return null;
    }

    set({ translationCreateLoading: true, translationCreateError: "" });

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const relationAttempts = [];
    if (sentenceId != null && Number.isFinite(Number(sentenceId))) {
      relationAttempts.push({ sentence: Number(sentenceId) });
    }
    if (sentenceDocumentId) {
      relationAttempts.push({ sentence: { connect: [sentenceDocumentId] } });
    }
    if (!relationAttempts.length) {
      relationAttempts.push({ sentence: sentenceId ?? sentenceDocumentId });
    }

    const postTo = async (endpoint, payload) => {
      const resp = await fetch(base + endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        let message = "Ошибка создания перевода";
        try {
          const errData = await resp.json();
          message = errData?.error?.message || errData?.message || message;
        } catch {
          const text = await resp.text();
          if (text) message = text;
        }
        throw new Error(message);
      }

      return resp.json();
    };

    try {
      let lastError = null;

      for (const sentenceRel of relationAttempts) {
        const payload = {
          data: {
            ...sentenceRel,
            data: { signs: ordered },
            language: safeLang,
            reviewed: !!reviewed,
            notes: String(notes || "").trim(),
            user: Number(userId),
          },
        };

        try {
          let result;
          try {
            result = await postTo("/api/translations", payload);
          } catch {
            result = await postTo("/translations", payload);
          }
          set({ translationCreateError: "" });
          return result?.data ?? result;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError || new Error("Ошибка создания перевода");
    } catch (error) {
      set({ translationCreateError: error.message || "Ошибка создания перевода" });
      return null;
    } finally {
      set({ translationCreateLoading: false });
    }
  },

  fetchSigns: async (page = 1, pageSize = 6, query = "", queryField = "name") => {
    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const safeQuery = (query || "").trim();
    const safeField = queryField === "sign_id" ? "sign_id" : "name";
    const searchVariants = safeField === "name" ? buildYoSearchVariants(safeQuery) : [];
    const formattedQuery = safeField === "name" ? (searchVariants[0] || "") : safeQuery;

    set({ signsLoading: true, signsError: "" });

    const fetchSignssJson = async (endpoint, params) => {
      const url = `${base}${endpoint}?${params.toString()}`;
      const resp = await fetch(url, { headers });

      if (!resp.ok) {
        let message = "Ошибка загрузки жестов";
        try {
          const errData = await resp.json();
          message = errData?.error?.message || message;
        } catch {
          const text = await resp.text();
          if (text) message = text;
        }
        throw new Error(message);
      }

      return resp.json();
    };

    const fetchSignssPage = async (endpoint, filterField, filterValue, pageNum, limit) => {
      const params = new URLSearchParams();
      params.append("pagination[page]", String(pageNum));
      params.append("pagination[pageSize]", String(limit));
      params.append("populate", "sign_videos");
      params.append("populate", "word_associations");

      if (filterValue) {
        params.append(`filters[${filterField}][$contains][0]`, filterValue);
      }

      return fetchSignssJson(endpoint, params);
    };

    const fetchAllSignssByNameContains = async (nameQueries) => {
      const batchSize = 100;
      const maxPages = 40;
      const collected = [];
      const seen = new Set();
      const safeQueries = uniqueNonEmpty(nameQueries);

      const pushUniqueSigns = (items) => {
        for (const sign of (items || []).map(flattenEntity).filter(Boolean)) {
          const key = sign?.documentId || sign?.id;
          if (!key || seen.has(String(key))) continue;
          seen.add(String(key));
          collected.push(sign);
        }
      };

      const run = async (endpoint, nameQuery) => {
        let pageNum = 1;
        let pageCount = 1;
        while (pageNum <= pageCount && pageNum <= maxPages) {
          const payload = await fetchSignssPage(endpoint, "name", nameQuery, pageNum, batchSize);
          const items = Array.isArray(payload) ? payload : (payload?.data || []);
          const meta = payload?.meta?.pagination || {};
          pageCount = meta.pageCount || 1;
          pushUniqueSigns(items);
          if (!items.length || items.length < batchSize) break;
          pageNum += 1;
        }
      };

      for (const nameQuery of safeQueries) {
        try {
          await run("/signss", nameQuery);
        } catch {
          await run("/api/signss", nameQuery);
        }
      }

      return collected;
    };

    const fetchAssociationPagesByLemma = async (lemmaQueries) => {
      const batchSize = 100;
      const maxPages = 40;
      const raw = [];
      const seen = new Set();
      const safeQueries = uniqueNonEmpty(lemmaQueries);

      const pushUniqueAssociations = (items) => {
        for (const item of items || []) {
          const entity = flattenEntity(item) || item;
          const key = entity?.documentId || entity?.id;
          if (!key || seen.has(String(key))) continue;
          seen.add(String(key));
          raw.push(item);
        }
      };

      const run = async (endpoint, lemmaQuery) => {
        let pageNum = 1;
        let pageCount = 1;
        while (pageNum <= pageCount && pageNum <= maxPages) {
          const params = new URLSearchParams();
          params.append("pagination[page]", String(pageNum));
          params.append("pagination[pageSize]", String(batchSize));
          params.append("populate", "*");
          params.append("filters[words][lemma][$contains][0]", lemmaQuery);

          const payload = await fetchSignssJson(endpoint, params);
          const items = Array.isArray(payload) ? payload : (payload?.data || []);
          const meta = payload?.meta?.pagination || {};
          pageCount = meta.pageCount || 1;
          pushUniqueAssociations(items);
          if (!items.length || items.length < batchSize) break;
          pageNum += 1;
        }
      };

      try {
        for (const lemmaQuery of safeQueries) {
          try {
            await run("/word-associations", lemmaQuery);
          } catch {
            await run("/api/word-associations", lemmaQuery);
          }
        }
      } catch {
        return [];
      }

      return raw;
    };

    const buildWordScoresBySignId = (associationItems, queryVariants) => {
      const scores = new Map();

      for (const item of associationItems || []) {
        const association = flattenEntity(item);
        if (!association) continue;

        const signsList = [
          ...normalizeAssociationSignsRelation(association?.signs),
          ...normalizeAssociationSignsRelation(association?.signss),
        ];
        const wordsList = Array.isArray(association?.words) ? association.words : [];

        let associationWordScore = 0;
        for (const word of wordsList) {
          const lemma = word?.lemma || word?.name || "";
          associationWordScore = Math.max(
            associationWordScore,
            getBestSimilarityToVariants(lemma, queryVariants)
          );
        }

        for (const sign of signsList) {
          const signKey = String(sign?.documentId || sign?.id || "");
          if (!signKey) continue;

          const prev = scores.get(signKey) || 0;
          if (associationWordScore > prev) {
            scores.set(signKey, associationWordScore);
          }
        }
      }

      return scores;
    };

    const rankSignsByRelevance = (items, queryVariants, wordScoresBySignId) => {
      const ranked = (items || []).map((item, apiIndex) => {
        const signKey = String(item?.documentId || item?.id || "");
        const signScore = Math.max(
          getBestSimilarityToVariants(item?.name || "", queryVariants),
          getBestSimilarityToVariants(item?.lemma || "", queryVariants)
        );
        const wordScore = signKey ? (wordScoresBySignId.get(signKey) || 0) : 0;

        const hasSignMatch = signScore >= SEARCH_MATCH_THRESHOLD;
        const hasWordMatch = wordScore >= SEARCH_MATCH_THRESHOLD;

        let priority = 4;
        if (hasSignMatch && hasWordMatch) {
          priority = 1;
        } else if (hasSignMatch) {
          priority = 2;
        } else if (hasWordMatch) {
          priority = 3;
        }

        return {
          item,
          apiIndex,
          priority,
          signScore,
          wordScore,
        };
      });

      ranked.sort((left, right) => {
        if (left.priority !== right.priority) return left.priority - right.priority;
        const leftTotal = left.signScore + left.wordScore;
        const rightTotal = right.signScore + right.wordScore;
        if (leftTotal !== rightTotal) return rightTotal - leftTotal;
        if (left.signScore !== right.signScore) return right.signScore - left.signScore;
        if (left.wordScore !== right.wordScore) return right.wordScore - left.wordScore;
        return left.apiIndex - right.apiIndex;
      });

      return ranked.map((entry) => entry.item);
    };

    const collectAssociationSignsFromItems = (rawItems) => {
      const result = [];
      const seen = new Set();

      for (const item of rawItems || []) {
        const association = flattenEntity(item) || item;
        const signsList = [
          ...normalizeAssociationSignsRelation(association?.signs),
          ...normalizeAssociationSignsRelation(association?.signss),
        ];

        for (const sign of signsList) {
          const key = String(sign?.documentId || sign?.id || "");
          if (!key || seen.has(key)) continue;
          seen.add(key);
          result.push(sign);
        }
      }

      return result;
    };

    const collectSignKeysFromAssociationItems = (rawItems) => {
      const ids = new Set();
      const documentIds = new Set();
      const associationSigns = collectAssociationSignsFromItems(rawItems);
      for (const sign of associationSigns) {
        const numericId = Number(sign?.id);
        if (Number.isFinite(numericId)) ids.add(numericId);
        if (sign?.documentId) documentIds.add(String(sign.documentId));
      }
      return {
        ids: Array.from(ids),
        documentIds: Array.from(documentIds),
      };
    };

    const fetchSignssByFilterChunks = async (filterField, values) => {
      const filteredValues = uniqueNonEmpty(values);
      if (!filteredValues.length) return [];

      const chunkSize = 50;
      const chunks = [];
      for (let i = 0; i < filteredValues.length; i += chunkSize) {
        chunks.push(filteredValues.slice(i, i + chunkSize));
      }

      const out = [];
      const seen = new Set();
      for (const chunk of chunks) {
        const params = new URLSearchParams();
        chunk.forEach((value, index) => {
          params.append(`filters[${filterField}][$in][${index}]`, String(value));
        });
        params.append("populate", "sign_videos");
        params.append("populate", "word_associations");
        params.append("pagination[page]", "1");
        params.append("pagination[pageSize]", String(Math.max(chunk.length, 1)));

        try {
          const payload = await fetchSignssJson("/signss", params);
          const items = Array.isArray(payload) ? payload : (payload?.data || []);
          for (const sign of (items || []).map(flattenEntity).filter(Boolean)) {
            const key = String(sign?.documentId || sign?.id || "");
            if (!key || seen.has(key)) continue;
            seen.add(key);
            out.push(sign);
          }
        } catch {
          const payload = await fetchSignssJson("/api/signss", params);
          const items = Array.isArray(payload) ? payload : (payload?.data || []);
          for (const sign of (items || []).map(flattenEntity).filter(Boolean)) {
            const key = String(sign?.documentId || sign?.id || "");
            if (!key || seen.has(key)) continue;
            seen.add(key);
            out.push(sign);
          }
        }
      }
      return out;
    };

    const signsQueryValue = safeField === "name" ? formattedQuery : safeQuery;

    try {
      if (safeField === "name" && formattedQuery) {
        const nameSigns = await fetchAllSignssByNameContains(searchVariants);
        const assocRaw = await fetchAssociationPagesByLemma(searchVariants);
        const assocSigns = collectAssociationSignsFromItems(assocRaw);

        const wordSignKeys = collectSignKeysFromAssociationItems(assocRaw);
        const nameIdSet = new Set(nameSigns.map((s) => Number(s?.id)).filter((id) => Number.isFinite(id)));
        const nameDocumentIdSet = new Set(
          nameSigns.map((s) => String(s?.documentId || "").trim()).filter(Boolean)
        );
        const extraIds = (wordSignKeys.ids || []).filter((id) => !nameIdSet.has(id));
        const extraDocumentIds = (wordSignKeys.documentIds || [])
          .map((value) => String(value).trim())
          .filter((value) => value && !nameDocumentIdSet.has(value));
        let wordSigns = [];

        if (extraIds.length || extraDocumentIds.length) {
          const idQueryValues = uniqueNonEmpty(extraIds.map((id) => String(id)));
          const docQueryValues = uniqueNonEmpty(extraDocumentIds.map((id) => String(id)));

          try {
            const byIds = idQueryValues.length
              ? await fetchSignssByFilterChunks("id", idQueryValues)
              : [];
            const byDocumentIds = docQueryValues.length
              ? await fetchSignssByFilterChunks("documentId", docQueryValues)
              : [];

            const mergedByKeys = [];
            const mergedSeen = new Set();
            for (const sign of [...byIds, ...byDocumentIds]) {
              const key = String(sign?.documentId || sign?.id || "");
              if (!key || mergedSeen.has(key)) continue;
              mergedSeen.add(key);
              mergedByKeys.push(sign);
            }
            wordSigns = mergedByKeys;
          } catch {
            wordSigns = [];
          }
        }

        if (!wordSigns.length) {
          wordSigns = assocSigns;
        } else if (assocSigns.length) {
          const mergedFallback = [...wordSigns];
          const mergedSeen = new Set(
            mergedFallback.map((sign) => String(sign?.documentId || sign?.id || "")).filter(Boolean)
          );
          for (const sign of assocSigns) {
            const key = String(sign?.documentId || sign?.id || "");
            if (!key || mergedSeen.has(key)) continue;
            mergedSeen.add(key);
            mergedFallback.push(sign);
          }
          wordSigns = mergedFallback;
        }

        const merged = [...nameSigns];
        const seen = new Set(
          nameSigns.map((s) => String(s?.documentId || s?.id || "")).filter(Boolean)
        );
        for (const s of wordSigns) {
          const key = String(s?.documentId || s?.id || "");
          if (!key || seen.has(key)) continue;
          seen.add(key);
          merged.push(s);
        }

        const wordScoresBySignId = buildWordScoresBySignId(assocRaw, searchVariants);
        const ranked = rankSignsByRelevance(merged, searchVariants, wordScoresBySignId);

        const total = ranked.length;
        const safePageSize = Math.max(1, Number(pageSize) || 6);
        const safePage = Math.max(1, Number(page) || 1);
        const pageCount = Math.max(1, Math.ceil(total / safePageSize));
        const start = (safePage - 1) * safePageSize;
        const pageSlice = ranked.slice(start, start + safePageSize);

        set({
          signs: pageSlice,
          signsError: "",
          signsQuery: signsQueryValue,
          signsPagination: {
            page: safePage,
            pageSize: safePageSize,
            pageCount,
            total,
          },
        });
        return pageSlice;
      }

      let payload;
      try {
        payload = await fetchSignssPage("/signss", safeField, safeField === "sign_id" ? safeQuery : formattedQuery, page, pageSize);
      } catch {
        payload = await fetchSignssPage("/api/signss", safeField, safeField === "sign_id" ? safeQuery : formattedQuery, page, pageSize);
      }

      const items = Array.isArray(payload) ? payload : (payload?.data || [payload]);
      const meta = payload?.meta?.pagination || {};
      const normalized = (items || []).map(flattenEntity).filter(Boolean);

      set({
        signs: normalized,
        signsError: "",
        signsQuery: signsQueryValue,
        signsPagination: {
          page: meta.page || page,
          pageSize: meta.pageSize || pageSize,
          pageCount: meta.pageCount || 1,
          total: meta.total ?? normalized.length,
        },
      });
      return normalized;
    } catch (error) {
      set({
        signs: [],
        signsError: error.message || "Ошибка загрузки жестов",
        signsQuery: signsQueryValue,
        signsPagination: {
          page,
          pageSize,
          pageCount: 1,
          total: 0,
        },
      });
      return [];
    } finally {
      set({ signsLoading: false });
    }
  },

  createSign: async ({ name, sign_id }) => {
    const safeName = (name || "").trim();
    const safeSignId = (sign_id || "").trim();

    if (!safeName || !safeSignId) {
      set({ createSignError: "Заполните поля name и sign_id" });
      return null;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    set({ createSignLoading: true, createSignError: "" });

    const payload = { data: { name: safeName, sign_id: safeSignId } };

    try {
      const postTo = async (endpoint) => {
        const resp = await fetch(base + endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify(payload),
        });

        if (!resp.ok) {
          let message = "Ошибка создания жеста";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }

        return resp.json();
      };

      let result;
      try {
        result = await postTo("/signss");
      } catch {
        result = await postTo("/api/signss");
      }

      set({ createSignError: "" });
      return flattenEntity(result?.data || result);
    } catch (error) {
      set({ createSignError: error.message || "Ошибка создания жеста" });
      return null;
    } finally {
      set({ createSignLoading: false });
    }
  },

  deleteSign: async (sign) => {
    const id = sign?.id;
    const documentId = sign?.documentId;
    if (!id && !documentId) {
      set({ deleteSignError: "Не найден идентификатор жеста" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const pathCandidates = [];
    if (documentId) {
      pathCandidates.push(`/signss/${documentId}`, `/api/signss/${documentId}`);
    }
    if (id) {
      pathCandidates.push(`/signss/${id}`, `/api/signss/${id}`);
    }

    set({ deleteSignLoading: true, deleteSignError: "" });

    try {
      let lastError = null;

      for (const path of pathCandidates) {
        try {
          const resp = await fetch(base + path, {
            method: "DELETE",
            headers,
          });

          if (resp.ok) {
            set({ deleteSignError: "" });
            return true;
          }

          let message = `Ошибка удаления жеста (${resp.status})`;
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          lastError = new Error(message);
        } catch (e) {
          lastError = e;
        }
      }

      throw lastError || new Error("Ошибка удаления жеста");
    } catch (error) {
      set({ deleteSignError: error.message || "Ошибка удаления жеста" });
      return false;
    } finally {
      set({ deleteSignLoading: false });
    }
  },

  fetchSignVideosByIds: async (ids = [], force = false) => {
    const cleanIds = Array.from(new Set((ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))));
    if (!cleanIds.length) return {};

    const state = get();
    const targetIds = force ? cleanIds : cleanIds.filter((id) => !state.signVideosById[id]);
    if (!targetIds.length) {
      return state.signVideosById;
    }

    const base = (state.API || API_BASE).replace(/\/$/, "");
    const token = state.token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const buildUrl = (endpoint) => {
      const params = new URLSearchParams();
      targetIds.forEach((id, index) => {
        params.append(`filters[id][$in][${index}]`, String(id));
      });
      params.append("populate", "*");
      return `${base}${endpoint}?${params.toString()}`;
    };

    set({ signVideosLoading: true, signVideosError: "" });

    try {
      const fetchFrom = async (endpoint) => {
        const resp = await fetch(buildUrl(endpoint), { headers });
        if (!resp.ok) {
          let message = "Ошибка загрузки sign_videos";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return resp.json();
      };

      let payload;
      try {
        payload = await fetchFrom("/sign-videos");
      } catch {
        payload = await fetchFrom("/api/sign-videos");
      }

      const rawItems = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalizedItems = rawItems.map(flattenEntity).filter(Boolean);
      const mergedMap = { ...get().signVideosById };
      normalizedItems.forEach((item) => {
        if (item?.id) mergedMap[item.id] = item;
      });

      set({ signVideosById: mergedMap, signVideosError: "" });
      return mergedMap;
    } catch (error) {
      set({ signVideosError: error.message || "Ошибка загрузки sign_videos" });
      return get().signVideosById;
    } finally {
      set({ signVideosLoading: false });
    }
  },

  createSignVideoForSign: async (sign) => {
    const signId = Number(sign?.id);
    const signDocumentId = sign?.documentId;

    if (!Number.isFinite(signId) && !signDocumentId) {
      set({ signVideoUploadError: "Не найден ID жеста для создания SignVideo" });
      return null;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    set({ signVideoUploadLoading: true, signVideoUploadError: "" });

    try {
      const payloads = [
        ...(signDocumentId ? [{ data: { signs: { connect: [signDocumentId] } } }] : []),
        ...(signDocumentId ? [{ data: { signs: { connect: [{ documentId: signDocumentId }] } } }] : []),
        ...(Number.isFinite(signId) ? [{ data: { signs: { connect: [signId] } } }] : []),
        ...(Number.isFinite(signId) ? [{ data: { signs: { connect: [{ id: signId }] } } }] : []),
        ...(signDocumentId ? [{ data: { signss: { connect: [signDocumentId] } } }] : []),
        ...(signDocumentId ? [{ data: { signss: { connect: [{ documentId: signDocumentId }] } } }] : []),
        ...(Number.isFinite(signId) ? [{ data: { signss: { connect: [signId] } } }] : []),
        ...(Number.isFinite(signId) ? [{ data: { signss: { connect: [{ id: signId }] } } }] : []),
        { data: {} },
      ];

      let lastError = null;

      for (const endpoint of ["/sign-videos", "/api/sign-videos"]) {
        for (const payload of payloads) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "POST",
              headers,
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              let message = "Ошибка создания SignVideo";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || errData?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            const result = await resp.json();
            const created = flattenEntity(result?.data || result);
            if (created?.id) {
              await get().fetchSignVideosByIds([created.id], true);
            }
            set({ signVideoUploadError: "" });
            return created;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка создания SignVideo");
    } catch (error) {
      set({ signVideoUploadError: error.message || "Ошибка создания SignVideo" });
      return null;
    } finally {
      set({ signVideoUploadLoading: false });
    }
  },

  uploadSignVideoFile: async (signVideo, file) => {
    const signVideoId = Number(signVideo?.id);

    if (!Number.isFinite(signVideoId)) {
      set({ signVideoUploadError: "Не найден ID SignVideo" });
      return false;
    }

    if (!file) {
      set({ signVideoUploadError: "Выберите файл для загрузки" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    set({ signVideoUploadLoading: true, signVideoUploadError: "" });

    const parseErrorMessage = async (resp, fallback) => {
      let message = fallback;
      try {
        const errData = await resp.json();
        message = errData?.error?.message || errData?.message || message;
      } catch {
        const text = await resp.text();
        if (text) message = text;
      }
      return message;
    };

    const postUpload = async (endpoint, extras = []) => {
      const formData = new FormData();
      formData.append("files", file);
      extras.forEach(([key, value]) => {
        if (value !== null && typeof value !== "undefined" && String(value) !== "") {
          formData.append(key, String(value));
        }
      });

      const resp = await fetch(base + endpoint, {
        method: "POST",
        headers: authHeaders,
        body: formData,
      });

      if (!resp.ok) {
        const message = await parseErrorMessage(resp, "Ошибка загрузки видео");
        throw new Error(message);
      }

      return resp.json();
    };

    const extractMediaIds = (payload) => {
      const items = Array.isArray(payload)
        ? payload
        : (Array.isArray(payload?.data) ? payload.data : []);

      return items
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id));
    };

    const attachUploadedMedia = async (mediaIds, lastErrorRef) => {
      if (!mediaIds.length) return false;

      const mediaId = mediaIds[mediaIds.length - 1];
      const updatePayloads = [
        { data: { video: mediaId } },
        { data: { video: [mediaId] } },
        { data: { video: { set: [mediaId] } } },
        { data: { video: { set: [{ id: mediaId }] } } },
        { data: { video: { connect: [mediaId] } } },
        { data: { video: { connect: [{ id: mediaId }] } } },
      ];

      const keys = Array.from(new Set([signVideo?.documentId, signVideoId].filter(Boolean)));
      for (const key of keys) {
        for (const endpoint of [`/sign-videos/${key}`, `/api/sign-videos/${key}`]) {
          for (const payload of updatePayloads) {
            try {
              const resp = await fetch(base + endpoint, {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  ...authHeaders,
                },
                body: JSON.stringify(payload),
              });

              if (!resp.ok) {
                const message = await parseErrorMessage(resp, "Ошибка привязки файла к SignVideo");
                throw new Error(message);
              }

              await get().fetchSignVideosByIds([signVideoId], true);
              set({ signVideoUploadError: "" });
              return true;
            } catch (error) {
              lastErrorRef.current = error;
            }
          }
        }
      }

      return false;
    };

    try {
      const lastErrorRef = { current: null };
      const uploadEndpoints = ["/api/upload", "/upload"];
      const refCandidates = [
        [
          ["ref", "api::sign-video.sign-video"],
          ["refId", signVideoId],
          ["field", "video"],
        ],
        [
          ["ref", "api::sign-video.sign-video"],
          ["refId", signVideo?.documentId || signVideoId],
          ["field", "video"],
        ],
      ];


      let uploadedMediaIds = [];

      outerRefUpload:
      for (const endpoint of uploadEndpoints) {
        for (const extras of refCandidates) {
          try {
            const payload = await postUpload(endpoint, extras);
            uploadedMediaIds = extractMediaIds(payload);
            break outerRefUpload;
          } catch (error) {
            lastErrorRef.current = error;
          }
        }
      }

      if (uploadedMediaIds.length) {
        const linked = await attachUploadedMedia(uploadedMediaIds, lastErrorRef);
        if (linked) {
          return true;
        }
      }

      let uploadedPayload = null;
      for (const endpoint of uploadEndpoints) {
        try {
          uploadedPayload = await postUpload(endpoint, []);
          break;
        } catch (error) {
          lastErrorRef.current = error;
        }
      }

      const mediaIds = extractMediaIds(uploadedPayload);

      if (!mediaIds.length) {
        throw lastErrorRef.current || new Error("Не удалось получить ID загруженного файла");
      }

      const linked = await attachUploadedMedia(mediaIds, lastErrorRef);
      if (linked) {
        return true;
      }

      throw lastErrorRef.current || new Error("Ошибка загрузки файла SignVideo");
    } catch (error) {
      set({ signVideoUploadError: error.message || "Ошибка загрузки файла SignVideo" });
      return false;
    } finally {
      set({ signVideoUploadLoading: false });
    }
  },

  removeSignVideoFile: async (signVideo) => {
    const signVideoId = Number(signVideo?.id);
    const signVideoDocumentId = signVideo?.documentId;

    if (!Number.isFinite(signVideoId) && !signVideoDocumentId) {
      set({ signVideoUploadError: "Не найден ID SignVideo" });
      return false;
    }

    const mediaIds = (Array.isArray(signVideo?.video) ? signVideo.video : [])
      .map((item) => Number(item?.id))
      .filter((id) => Number.isFinite(id));

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

    set({ signVideoUploadLoading: true, signVideoUploadError: "" });

    const parseErrorMessage = async (resp, fallback) => {
      let message = fallback;
      try {
        const errData = await resp.json();
        message = errData?.error?.message || errData?.message || message;
      } catch {
        const text = await resp.text();
        if (text) message = text;
      }
      return message;
    };

    try {
      const keys = Array.from(new Set([signVideoDocumentId, signVideoId].filter(Boolean)));
      const detachPayloads = [
        { data: { video: null } },
        { data: { video: [] } },
        { data: { video: { set: [] } } },
        ...(mediaIds.length ? [{ data: { video: { disconnect: mediaIds } } }] : []),
        ...(mediaIds.length ? [{ data: { video: { disconnect: mediaIds.map((id) => ({ id })) } } }] : []),
      ];

      let detachLastError = null;
      let detached = false;

      for (const key of keys) {
        for (const endpoint of [`/sign-videos/${key}`, `/api/sign-videos/${key}`]) {
          for (const payload of detachPayloads) {
            try {
              const resp = await fetch(base + endpoint, {
                method: "PUT",
                headers: {
                  "Content-Type": "application/json",
                  ...authHeaders,
                },
                body: JSON.stringify(payload),
              });

              if (!resp.ok) {
                const message = await parseErrorMessage(resp, "Ошибка отвязки файла от SignVideo");
                throw new Error(message);
              }

              detached = true;
              detachLastError = null;
              break;
            } catch (error) {
              detachLastError = error;
            }
          }
          if (detached) break;
        }
        if (detached) break;
      }

      if (!detached) {
        throw detachLastError || new Error("Не удалось отвязать файл от SignVideo");
      }

      if (Number.isFinite(signVideoId)) {
        set((state) => {
          const current = state.signVideosById?.[signVideoId] || signVideo;
          if (!current) return {};

          return {
            signVideosById: {
              ...state.signVideosById,
              [signVideoId]: {
                ...current,
                video: [],
              },
            },
          };
        });
      }

      for (const mediaId of mediaIds) {
        let removed = false;
        let deleteLastError = null;
        for (const endpoint of [`/api/upload/files/${mediaId}`, `/upload/files/${mediaId}`]) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "DELETE",
              headers: authHeaders,
            });

            if (!resp.ok) {
              const message = await parseErrorMessage(resp, "Ошибка удаления файла");
              throw new Error(message);
            }

            removed = true;
            deleteLastError = null;
            break;
          } catch (error) {
            deleteLastError = error;
          }
        }

        if (!removed) {
          throw deleteLastError || new Error("Не удалось удалить файл с сервера");
        }
      }

      if (Number.isFinite(signVideoId)) {
        await get().fetchSignVideosByIds([signVideoId], true);
      }
      set({ signVideoUploadError: "" });
      return true;
    } catch (error) {
      set({ signVideoUploadError: error.message || "Ошибка удаления файла SignVideo" });
      return false;
    } finally {
      set({ signVideoUploadLoading: false });
    }
  },

  updateSignVideoSimilar: async (signVideo, similar) => {
    const signVideoId = Number(signVideo?.id);
    const signVideoDocumentId = signVideo?.documentId;

    if (!Number.isFinite(signVideoId) && !signVideoDocumentId) {
      set({ signVideoUploadError: "Не найден ID SignVideo" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    set({ signVideoUploadLoading: true, signVideoUploadError: "" });

    try {
      const payloads = [
        { data: { metadata: { ...(signVideo?.metadata || {}), similar: !!similar } } },
        { data: { similar: !!similar } },
      ];

      const keys = Array.from(new Set([signVideoDocumentId, signVideoId].filter(Boolean)));
      let lastError = null;

      for (const key of keys) {
        for (const endpoint of [`/sign-videos/${key}`, `/api/sign-videos/${key}`]) {
          for (const payload of payloads) {
            try {
              const resp = await fetch(base + endpoint, {
                method: "PUT",
                headers,
                body: JSON.stringify(payload),
              });

              if (!resp.ok) {
                let message = "Ошибка обновления similar";
                try {
                  const errData = await resp.json();
                  message = errData?.error?.message || errData?.message || message;
                } catch {
                  const text = await resp.text();
                  if (text) message = text;
                }
                throw new Error(message);
              }

              if (Number.isFinite(signVideoId)) {
                set((state) => {
                  const current = state.signVideosById?.[signVideoId] || signVideo;
                  return {
                    signVideosById: {
                      ...state.signVideosById,
                      [signVideoId]: {
                        ...current,
                        metadata: {
                          ...(current?.metadata || {}),
                          similar: !!similar,
                        },
                        similar: !!similar,
                      },
                    },
                  };
                });
                await get().fetchSignVideosByIds([signVideoId], true);
              }

              set({ signVideoUploadError: "" });
              return true;
            } catch (error) {
              lastError = error;
            }
          }
        }
      }

      throw lastError || new Error("Ошибка обновления similar");
    } catch (error) {
      set({ signVideoUploadError: error.message || "Ошибка обновления similar" });
      return false;
    } finally {
      set({ signVideoUploadLoading: false });
    }
  },

  fetchWordAssociationsByIds: async (ids = [], force = false) => {
    const cleanIds = Array.from(new Set((ids || []).map((id) => Number(id)).filter((id) => Number.isFinite(id))));
    if (!cleanIds.length) return {};

    const state = get();
    const targetIds = force ? cleanIds : cleanIds.filter((id) => !state.wordAssociationsById[id]);
    if (!targetIds.length) {
      return state.wordAssociationsById;
    }

    const base = (state.API || API_BASE).replace(/\/$/, "");
    const token = state.token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    const buildUrl = (endpoint) => {
      const params = new URLSearchParams();
      targetIds.forEach((id, index) => {
        params.append(`filters[id][$in][${index}]`, String(id));
      });
      params.append("populate", "words");
      params.append("populate", "signs");
      return `${base}${endpoint}?${params.toString()}`;
    };

    set({ wordAssociationsLoading: true, wordAssociationsError: "" });

    try {
      const fetchFrom = async (endpoint) => {
        const resp = await fetch(buildUrl(endpoint), { headers });
        if (!resp.ok) {
          let message = "Ошибка загрузки word_associations";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return resp.json();
      };

      let payload;
      try {
        payload = await fetchFrom("/word-associations");
      } catch {
        payload = await fetchFrom("/api/word-associations");
      }

      const rawItems = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalizedItems = rawItems.map(flattenEntity).filter(Boolean);
      const mergedMap = { ...get().wordAssociationsById };
      normalizedItems.forEach((item) => {
        if (item?.id) mergedMap[item.id] = item;
      });

      set({ wordAssociationsById: mergedMap, wordAssociationsError: "" });
      return mergedMap;
    } catch (error) {
      set({ wordAssociationsError: error.message || "Ошибка загрузки word_associations" });
      return get().wordAssociationsById;
    } finally {
      set({ wordAssociationsLoading: false });
    }
  },

  searchAssociationSigns: async (query, pageSize = 10) => {
    const formattedQuery = formatGestureName(query);
    if (!formattedQuery) {
      set({ associationSignsSearchResults: [], associationSignsSearchError: "" });
      return [];
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    set({ associationSignsSearchLoading: true, associationSignsSearchError: "" });

    const fetchFrom = async (endpoint) => {
      const params = new URLSearchParams();
      params.append("filters[name][$contains][0]", formattedQuery);
      params.append("pagination[page]", "1");
      params.append("pagination[pageSize]", String(pageSize));
      const resp = await fetch(`${base}${endpoint}?${params.toString()}`, { headers });

      if (!resp.ok) {
        let message = "Ошибка поиска жестов";
        try {
          const errData = await resp.json();
          message = errData?.error?.message || message;
        } catch {
          const text = await resp.text();
          if (text) message = text;
        }
        throw new Error(message);
      }

      return resp.json();
    };

    try {
      let payload;
      try {
        payload = await fetchFrom("/signss");
      } catch {
        payload = await fetchFrom("/api/signss");
      }

      const items = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalized = items.map(flattenEntity).filter(Boolean);
      set({ associationSignsSearchResults: normalized, associationSignsSearchError: "" });
      return normalized;
    } catch (error) {
      set({ associationSignsSearchResults: [], associationSignsSearchError: error.message || "Ошибка поиска жестов" });
      return [];
    } finally {
      set({ associationSignsSearchLoading: false });
    }
  },

  attachSignToAssociation: async (association, sign) => {
    const associationKey = association?.documentId || association?.id;
    const associationId = association?.id;
    const signDocumentId = sign?.documentId;
    const signId = sign?.id;
    const signKey = signDocumentId || signId;

    if (!associationKey || !signKey) {
      set({ associationSignsMutationError: "Не найдены идентификаторы для привязки" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    set({ associationSignsMutationLoading: true, associationSignsMutationError: "" });

    const payloads = [
      ...(signDocumentId ? [{ data: { signs: { connect: [signDocumentId] } } }] : []),
      ...(signDocumentId ? [{ data: { signs: { connect: [{ documentId: signDocumentId }] } } }] : []),
      ...(signId ? [{ data: { signs: { connect: [signId] } } }] : []),
      ...(signId ? [{ data: { signs: { connect: [{ id: signId }] } } }] : []),
      ...(signId ? [{ data: { signs: [signId] } }] : []),
    ];

    try {
      let lastError = null;

      for (const endpoint of [`/word-associations/${associationKey}`, `/api/word-associations/${associationKey}`]) {
        for (const payload of payloads) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              let message = "Ошибка привязки жеста к ассоциации";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            if (associationId) {
              await get().fetchWordAssociationsByIds([associationId], true);
            }

            set({ associationSignsMutationError: "" });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка привязки жеста к ассоциации");
    } catch (error) {
      set({ associationSignsMutationError: error.message || "Ошибка привязки жеста к ассоциации" });
      return false;
    } finally {
      set({ associationSignsMutationLoading: false });
    }
  },

  removeSignFromAssociation: async (association, sign) => {
    const associationId = association?.id;
    const associationDocumentId = association?.documentId;
    const associationKey = associationDocumentId || associationId;
    const signId = sign?.id;
    const signDocumentId = sign?.documentId;

    if (!associationKey || (!signId && !signDocumentId)) {
      set({ associationSignsMutationError: "Не найдены идентификаторы для удаления привязки" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
    const headers = {
      "Content-Type": "application/json",
      ...authHeaders,
    };

    set({ associationSignsMutationLoading: true, associationSignsMutationError: "" });

    const normalizeSigns = (value) => {
      if (Array.isArray(value)) return value;
      if (Array.isArray(value?.data)) {
        return value.data.map((item) => (item?.attributes ? { id: item.id, ...item.attributes } : item));
      }
      return [];
    };

    try {
      const fetchAssociation = async (endpoint) => {
        const url = `${base}${endpoint}?populate=signs`;
        const resp = await fetch(url, { headers: authHeaders });
        if (!resp.ok) {
          let message = "Ошибка чтения association";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }
        return resp.json();
      };

      let associationPayload;
      try {
        associationPayload = await fetchAssociation(`/word-associations/${associationKey}`);
      } catch {
        associationPayload = await fetchAssociation(`/api/word-associations/${associationKey}`);
      }

      const associationEntity = flattenEntity(associationPayload?.data || associationPayload);
      const currentSigns = normalizeSigns(associationEntity?.signs);

      const remainingSigns = currentSigns.filter((item) => {
        const itemId = item?.id;
        const itemDocumentId = item?.documentId;
        if (signDocumentId && itemDocumentId && String(itemDocumentId) === String(signDocumentId)) return false;
        if (signId && itemId && Number(itemId) === Number(signId)) return false;
        return true;
      });

      const remainingByDocumentId = remainingSigns.map((item) => item?.documentId).filter(Boolean);
      const remainingById = remainingSigns
        .map((item) => Number(item?.id))
        .filter((id) => Number.isFinite(id));

      const payloads = [
        { data: { signs: remainingByDocumentId } },
        { data: { signs: { set: remainingByDocumentId } } },
        { data: { signs: { set: remainingByDocumentId.map((documentId) => ({ documentId })) } } },
        { data: { signs: remainingById } },
        { data: { signs: { set: remainingById } } },
        { data: { signs: { set: remainingById.map((id) => ({ id })) } } },
      ];

      let lastError = null;
      const keys = Array.from(new Set([associationDocumentId, associationId].filter(Boolean)));
      for (const key of keys) {
        for (const endpoint of [`/word-associations/${key}`, `/api/word-associations/${key}`]) {
          for (const payload of payloads) {
            try {
              const resp = await fetch(base + endpoint, {
                method: "PUT",
                headers,
                body: JSON.stringify(payload),
              });

              if (!resp.ok) {
                let message = "Ошибка удаления жеста из ассоциации";
                try {
                  const errData = await resp.json();
                  message = errData?.error?.message || message;
                } catch {
                  const text = await resp.text();
                  if (text) message = text;
                }
                throw new Error(message);
              }

              if (associationId) {
                await get().fetchWordAssociationsByIds([associationId], true);
              }

              set({ associationSignsMutationError: "" });
              return true;
            } catch (error) {
              lastError = error;
            }
          }
        }
      }

      throw lastError || new Error("Ошибка удаления жеста из ассоциации");
    } catch (error) {
      set({ associationSignsMutationError: error.message || "Ошибка удаления жеста из ассоциации" });
      return false;
    } finally {
      set({ associationSignsMutationLoading: false });
    }
  },

  searchAssociationWords: async (query, pageSize = 10) => {
    const formattedQuery = formatGestureName(query);
    if (!formattedQuery) {
      set({ associationWordsSearchResults: [], associationWordsSearchError: "" });
      return [];
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    set({ associationWordsSearchLoading: true, associationWordsSearchError: "" });

    const fetchFrom = async (endpoint) => {
      const params = new URLSearchParams();
      params.append("filters[lemma][$contains][0]", formattedQuery);
      params.append("pagination[page]", "1");
      params.append("pagination[pageSize]", String(pageSize));
      const resp = await fetch(`${base}${endpoint}?${params.toString()}`, { headers });

      if (!resp.ok) {
        let message = "Ошибка поиска слов";
        try {
          const errData = await resp.json();
          message = errData?.error?.message || message;
        } catch {
          const text = await resp.text();
          if (text) message = text;
        }
        throw new Error(message);
      }

      return resp.json();
    };

    try {
      let payload;
      try {
        payload = await fetchFrom("/words");
      } catch {
        payload = await fetchFrom("/api/words");
      }

      const items = Array.isArray(payload) ? payload : (payload?.data || []);
      const normalized = items.map(flattenEntity).filter(Boolean);
      set({ associationWordsSearchResults: normalized, associationWordsSearchError: "" });
      return normalized;
    } catch (error) {
      set({ associationWordsSearchResults: [], associationWordsSearchError: error.message || "Ошибка поиска слов" });
      return [];
    } finally {
      set({ associationWordsSearchLoading: false });
    }
  },

  attachWordToAssociation: async (association, word) => {
    const associationKey = association?.documentId || association?.id;
    const associationId = association?.id;
    const associationDocumentId = association?.documentId;
    const wordKey = word?.documentId || word?.id;

    if (!associationKey || !wordKey) {
      set({ associationWordsMutationError: "Не найдены идентификаторы для привязки слова" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    set({ associationWordsMutationLoading: true, associationWordsMutationError: "" });

    const payloads = [
      ...(associationDocumentId ? [{ data: { word_association: associationDocumentId } }] : []),
      ...(associationDocumentId ? [{ data: { word_association: { connect: [associationDocumentId] } } }] : []),
      ...(associationDocumentId ? [{ data: { word_association: { connect: [{ documentId: associationDocumentId }] } } }] : []),
      ...(associationId ? [{ data: { word_association: associationId } }] : []),
      ...(associationId ? [{ data: { word_association: { connect: [associationId] } } }] : []),
      ...(associationId ? [{ data: { word_association: { connect: [{ id: associationId }] } } }] : []),
    ];

    try {
      let lastError = null;

      for (const endpoint of [`/words/${wordKey}`, `/api/words/${wordKey}`]) {
        for (const payload of payloads) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              let message = "Ошибка привязки слова к ассоциации";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            if (associationId) {
              await get().fetchWordAssociationsByIds([associationId], true);
            }

            set({ associationWordsMutationError: "" });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка привязки слова к ассоциации");
    } catch (error) {
      set({ associationWordsMutationError: error.message || "Ошибка привязки слова к ассоциации" });
      return false;
    } finally {
      set({ associationWordsMutationLoading: false });
    }
  },

  removeWordFromAssociation: async (association, word) => {
    const associationId = association?.id;
    const associationDocumentId = association?.documentId;
    const wordKey = word?.documentId || word?.id;

    if (!wordKey) {
      set({ associationWordsMutationError: "Не найден идентификатор слова" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const payloads = [
      { data: { word_association: null } },
      ...(associationDocumentId ? [{ data: { word_association: { disconnect: [associationDocumentId] } } }] : []),
      ...(associationDocumentId ? [{ data: { word_association: { disconnect: [{ documentId: associationDocumentId }] } } }] : []),
      ...(associationId ? [{ data: { word_association: { disconnect: [associationId] } } }] : []),
      ...(associationId ? [{ data: { word_association: { disconnect: [{ id: associationId }] } } }] : []),
    ];

    set({ associationWordsMutationLoading: true, associationWordsMutationError: "" });

    try {
      let lastError = null;

      for (const endpoint of [`/words/${wordKey}`, `/api/words/${wordKey}`]) {
        for (const payload of payloads) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              let message = "Ошибка удаления слова из ассоциации";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            if (associationId) {
              await get().fetchWordAssociationsByIds([associationId], true);
            }

            set({ associationWordsMutationError: "" });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка удаления слова из ассоциации");
    } catch (error) {
      set({ associationWordsMutationError: error.message || "Ошибка удаления слова из ассоциации" });
      return false;
    } finally {
      set({ associationWordsMutationLoading: false });
    }
  },

  createWordInAssociation: async (association, lemma) => {
    const associationId = association?.id;
    const associationDocumentId = association?.documentId;
    const safeLemma = formatGestureName(lemma);

    if (!associationId) {
      set({ wordMutationError: "Не найден ID word_association" });
      return null;
    }

    if (!safeLemma) {
      set({ wordMutationError: "Введите слово (lemma)" });
      return null;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    set({
      wordMutationLoading: true,
      wordMutationError: "",
      associationWordsMutationLoading: true,
      associationWordsMutationError: "",
    });

    const payloads = [
      ...(associationDocumentId ? [{ data: { lemma: safeLemma, word_association: associationDocumentId } }] : []),
      ...(associationDocumentId ? [{ data: { lemma: safeLemma, word_association: { connect: [associationDocumentId] } } }] : []),
      ...(associationDocumentId ? [{ data: { lemma: safeLemma, word_association: { connect: [{ documentId: associationDocumentId }] } } }] : []),
      ...(associationId ? [{ data: { lemma: safeLemma, word_association: associationId } }] : []),
      ...(associationId ? [{ data: { lemma: safeLemma, word_association: { connect: [associationId] } } }] : []),
      ...(associationId ? [{ data: { lemma: safeLemma, word_association: { connect: [{ id: associationId }] } } }] : []),
    ];

    try {
      const postTo = async (endpoint, body) => {
        const resp = await fetch(base + endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify(body),
        });

        if (!resp.ok) {
          let message = "Ошибка добавления слова";
          try {
            const errData = await resp.json();
            message = errData?.error?.message || message;
          } catch {
            const text = await resp.text();
            if (text) message = text;
          }
          throw new Error(message);
        }

        return resp.json();
      };

      let result = null;
      let lastError = null;

      for (const endpoint of ["/words", "/api/words"]) {
        for (const payload of payloads) {
          try {
            result = await postTo(endpoint, payload);
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
          }
        }
        if (result) break;
      }

      if (!result) {
        throw lastError || new Error("Ошибка добавления слова");
      }

      await get().fetchWordAssociationsByIds([associationId], true);
      set({ wordMutationError: "", associationWordsMutationError: "" });
      return flattenEntity(result?.data || result);
    } catch (error) {
      set({
        wordMutationError: error.message || "Ошибка добавления слова",
        associationWordsMutationError: error.message || "Ошибка добавления слова",
      });
      return null;
    } finally {
      set({ wordMutationLoading: false, associationWordsMutationLoading: false });
    }
  },

  detachWordFromAssociation: async (word, associationId) => {
    const wordId = word?.id;
    const wordDocumentId = word?.documentId;

    if (!wordId && !wordDocumentId) {
      set({ wordMutationError: "Не найден ID слова" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;
    const headers = {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    const payloads = [
      { data: { word_association: null } },
      ...(associationId ? [{ data: { word_association: { disconnect: [associationId] } } }] : []),
    ];

    const ids = [wordDocumentId, wordId].filter(Boolean);
    const endpoints = [];
    ids.forEach((id) => {
      endpoints.push(`/words/${id}`, `/api/words/${id}`);
    });

    set({ wordMutationLoading: true, wordMutationError: "" });

    try {
      let lastError = null;

      for (const endpoint of endpoints) {
        for (const payload of payloads) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              let message = "Ошибка удаления слова из ассоциации";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            if (associationId) {
              await get().fetchWordAssociationsByIds([associationId], true);
            }

            set({ wordMutationError: "" });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка удаления слова из ассоциации");
    } catch (error) {
      set({ wordMutationError: error.message || "Ошибка удаления слова из ассоциации" });
      return false;
    } finally {
      set({ wordMutationLoading: false });
    }
  },

  fetchDashboardStats: async () => {
    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({
        dashboardStats: {
          loading: false,
          error: "Нет токена",
          requests: { total: 0, new: 0, processed: 0, avgRating: null, topByRating: [] },
          signs: { total: 0, withoutVideo: 0 },
          sentences: { total: 0, untranslated: 0, translated: 0 },
        },
      });
      return null;
    }

    set((state) => ({
      dashboardStats: { ...state.dashboardStats, loading: true, error: "" },
    }));

    const headers = { Authorization: `Bearer ${token}` };

    const fetchJson = async (endpoints, queryString = "") => {
      for (const endpoint of endpoints) {
        try {
          const resp = await fetch(`${base}${endpoint}${queryString}`, { headers });
          if (!resp.ok) continue;
          return resp.json();
        } catch {
          // try next endpoint
        }
      }
      return null;
    };

    const fetchTotal = async (endpoints, extraParams = "") => {
      const params = `pagination[page]=1&pagination[pageSize]=1${extraParams ? `&${extraParams}` : ""}`;
      const payload = await fetchJson(endpoints, `?${params}`);
      if (!payload) return 0;
      return payload?.meta?.pagination?.total ?? 0;
    };

    const fetchAllSignRequests = async () => {
      const allRawItems = await fetchAllSignRequestRawItems(base, headers);
      return allRawItems.map(normalizeSignRequest).filter(Boolean);
    };

    try {
      const [
        requests,
        signsTotal,
        signsWithoutVideo,
        sentencesTotal,
        sentencesUntranslated,
      ] = await Promise.all([
        fetchAllSignRequests(),
        fetchTotal(["/signss", "/api/signss"]),
        fetchTotal(["/signss", "/api/signss"], "filters[sign_videos][$null]=true"),
        fetchTotal(["/api/sentences", "/sentences"]),
        fetchTotal(["/api/sentences", "/sentences"], "filters[translations][$null]=true"),
      ]);

      const newCount = requests.filter((item) => !item.added).length;
      const processedCount = requests.filter((item) => item.added).length;
      const rated = requests.filter((item) => item.rating != null);
      const avgRating = rated.length
        ? rated.reduce((sum, item) => sum + item.rating, 0) / rated.length
        : null;
      const topByRating = [...rated]
        .sort((a, b) => b.rating - a.rating)
        .slice(0, 5)
        .map((item) => ({
          id: item.id,
          lemma: item.lemma,
          rating: item.rating,
          added: item.added,
        }));

      const translated = Math.max(0, sentencesTotal - sentencesUntranslated);
      const stats = {
        loading: false,
        error: "",
        requests: {
          total: requests.length,
          new: newCount,
          processed: processedCount,
          avgRating,
          topByRating,
        },
        signs: { total: signsTotal, withoutVideo: signsWithoutVideo },
        sentences: {
          total: sentencesTotal,
          untranslated: sentencesUntranslated,
          translated,
        },
      };

      set({ dashboardStats: stats });
      return stats;
    } catch (error) {
      const message = error.message || "Ошибка загрузки статистики";
      set((state) => ({
        dashboardStats: { ...state.dashboardStats, loading: false, error: message },
      }));
      return null;
    }
  },

  fetchSignRequests: async () => {
    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({ signRequests: [], signRequestsError: "Нет токена" });
      return [];
    }

    set({ signRequestsLoading: true, signRequestsError: "" });

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const allRawItems = await fetchAllSignRequestRawItems(base, headers);
      const items = allRawItems.map(normalizeSignRequest).filter(Boolean);

      set({ signRequests: items, signRequestsError: "" });
      return items;
    } catch (error) {
      set({ signRequestsError: error.message || "Ошибка загрузки заявок" });
      return [];
    } finally {
      set({ signRequestsLoading: false });
    }
  },

  updateSignRequestStatus: async (request, added) => {
    const requestId = request?.id;
    const requestDocumentId = request?.documentId;

    if (!requestId && !requestDocumentId) {
      set({ signRequestsMutationError: "Не найден ID заявки" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({ signRequestsMutationError: "Нет токена" });
      return false;
    }

    set({ signRequestsMutationLoading: true, signRequestsMutationError: "" });

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const payload = { data: { added: !!added } };
      const keys = Array.from(new Set([requestDocumentId, requestId].filter(Boolean)));
      let lastError = null;

      for (const key of keys) {
        for (const endpoint of [`/api/sign-requests/${key}`, `/sign-requests/${key}`]) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            });

            if (!resp.ok) {
              let message = "Ошибка обновления статуса заявки";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || errData?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            set((state) => ({
              signRequests: state.signRequests.map((item) => {
                const sameDocument = requestDocumentId && item?.documentId && String(item.documentId) === String(requestDocumentId);
                const sameId = requestId && item?.id && Number(item.id) === Number(requestId);
                if (!sameDocument && !sameId) return item;
                return { ...item, added: !!added };
              }),
              signRequestsMutationError: "",
            }));
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка обновления статуса заявки");
    } catch (error) {
      set({ signRequestsMutationError: error.message || "Ошибка обновления статуса заявки" });
      return false;
    } finally {
      set({ signRequestsMutationLoading: false });
    }
  },

  deleteSignRequest: async (request) => {
    const requestId = request?.id;
    const requestDocumentId = request?.documentId;

    if (!requestId && !requestDocumentId) {
      set({ signRequestsMutationError: "Не найден ID заявки" });
      return false;
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({ signRequestsMutationError: "Нет токена" });
      return false;
    }

    set({ signRequestsMutationLoading: true, signRequestsMutationError: "" });

    try {
      const headers = { Authorization: `Bearer ${token}` };
      const keys = Array.from(new Set([requestDocumentId, requestId].filter(Boolean)));
      let lastError = null;

      for (const key of keys) {
        for (const endpoint of [`/api/sign-requests/${key}`, `/sign-requests/${key}`]) {
          try {
            const resp = await fetch(base + endpoint, {
              method: "DELETE",
              headers,
            });

            if (!resp.ok) {
              let message = "Ошибка удаления заявки";
              try {
                const errData = await resp.json();
                message = errData?.error?.message || errData?.message || message;
              } catch {
                const text = await resp.text();
                if (text) message = text;
              }
              throw new Error(message);
            }

            set((state) => ({
              signRequests: state.signRequests.filter((item) => {
                const sameDocument = requestDocumentId && item?.documentId && String(item.documentId) === String(requestDocumentId);
                const sameId = requestId && item?.id && Number(item.id) === Number(requestId);
                return !(sameDocument || sameId);
              }),
              signRequestsMutationError: "",
            }));
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка удаления заявки");
    } catch (error) {
      set({ signRequestsMutationError: error.message || "Ошибка удаления заявки" });
      return false;
    } finally {
      set({ signRequestsMutationLoading: false });
    }
  },

  findSignRequestPlaceholderUsage: async (request) => {
    const searchTokens = buildRequestSearchTokens(request);

    if (!searchTokens.length) {
      set({ signRequestUsageError: "Не найден documentId заявки", signRequestUsageLoading: false });
      return { items: [], totalTranslations: 0, searchTokens: [] };
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({ signRequestUsageError: "Нет токена", signRequestUsageLoading: false });
      return { items: [], totalTranslations: 0, searchTokens };
    }

    set({ signRequestUsageLoading: true, signRequestUsageError: "" });

    try {
      const translations = await fetchAllTranslationsFromApi(base, token);
      const matches = translations.filter((item) => translationContainsRequest(item, request));

      return {
        items: mapTranslationUsageItems(matches),
        totalTranslations: translations.length,
        searchTokens,
      };
    } catch (error) {
      set({ signRequestUsageError: error.message || "Ошибка поиска использования заглушки" });
      return { items: [], totalTranslations: 0, searchTokens };
    } finally {
      set({ signRequestUsageLoading: false });
    }
  },

  findSignUsage: async (sign) => {
    const searchTokens = buildSignSearchTokens(sign);

    if (!searchTokens.length) {
      set({ signUsageError: "Не найден documentId жеста", signUsageLoading: false });
      return { items: [], totalTranslations: 0, searchTokens: [] };
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({ signUsageError: "Нет токена", signUsageLoading: false });
      return { items: [], totalTranslations: 0, searchTokens };
    }

    set({ signUsageLoading: true, signUsageError: "" });

    try {
      const translations = await fetchAllTranslationsFromApi(base, token);
      const matches = translations.filter((item) => (
        searchTokens.some((searchToken) => translationContainsSignToken(item, searchToken))
      ));

      return {
        items: mapTranslationUsageItems(matches),
        totalTranslations: translations.length,
        searchTokens,
      };
    } catch (error) {
      set({ signUsageError: error.message || "Ошибка поиска использования жеста" });
      return { items: [], totalTranslations: 0, searchTokens };
    } finally {
      set({ signUsageLoading: false });
    }
  },

  migrateTranslationSignsToDocumentIds: async ({ dryRun = false } = {}) => {
    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      return { ok: false, error: "Нет токена", updated: 0, scanned: 0 };
    }

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const fetchAllPaged = async (endpoint, paramsBuilder) => {
      const pageSize = 100;
      const collected = [];
      let page = 1;

      while (page <= 500) {
        const params = paramsBuilder({ page, pageSize });
        const resp = await fetch(`${base}${endpoint}?${params.toString()}`, { headers });
        if (!resp.ok) {
          throw new Error(`Ошибка загрузки ${endpoint}`);
        }
        const payload = await resp.json();
        const items = Array.isArray(payload) ? payload : (payload?.data || []);
        collected.push(...items);
        if (!items.length || items.length < pageSize) break;
        page += 1;
      }

      return collected;
    };

    const signItems = await fetchAllPaged("/api/signss", ({ page, pageSize }) => {
      const params = new URLSearchParams();
      params.append("pagination[page]", String(page));
      params.append("pagination[pageSize]", String(pageSize));
      return params;
    });

    const requestItems = await fetchAllPaged("/api/sign-requests", ({ page, pageSize }) => {
      const params = new URLSearchParams();
      params.append("pagination[page]", String(page));
      params.append("pagination[pageSize]", String(pageSize));
      params.append("status", "published");
      return params;
    });

    const signIdToDocumentId = new Map();
    signItems.forEach((item) => {
      const normalized = flattenEntity(item);
      const numericId = Number(normalized?.id);
      const documentId = getEntityDocumentId(normalized);
      if (Number.isFinite(numericId) && documentId) {
        signIdToDocumentId.set(numericId, documentId);
      }
    });

    const requestIdToDocumentId = new Map();
    requestItems.forEach((item) => {
      const normalized = normalizeSignRequest(item);
      const numericId = Number(normalized?.id);
      const documentId = getRequestDocumentId(normalized);
      if (Number.isFinite(numericId) && documentId) {
        requestIdToDocumentId.set(numericId, documentId);
      }
    });

    const maps = { signIdToDocumentId, requestIdToDocumentId };
    const translations = await fetchAllTranslationsFromApi(base, token);
    let updated = 0;

    for (const translation of translations) {
      const currentSigns = translation?.data?.signs;
      if (!Array.isArray(currentSigns) || !currentSigns.length) continue;

      const nextSigns = migrateSignEntriesToDocumentIds(currentSigns, maps);
      const changed = nextSigns.some((entry, index) => String(entry) !== String(currentSigns[index]));
      if (!changed) continue;

      updated += 1;
      if (dryRun) continue;

      const keys = Array.from(new Set([translation?.documentId, translation?.id].filter(Boolean)));
      const payload = {
        data: {
          data: {
            ...(translation?.data || {}),
            signs: nextSigns,
          },
        },
      };

      let saved = false;
      for (const key of keys) {
        for (const endpoint of [`/api/translations/${key}`, `/translations/${key}`]) {
          try {
            const resp = await fetch(`${base}${endpoint}`, {
              method: "PUT",
              headers,
              body: JSON.stringify(payload),
            });
            if (resp.ok) {
              saved = true;
              break;
            }
          } catch {
            // try next endpoint
          }
        }
        if (saved) break;
      }

      if (!saved) {
        return {
          ok: false,
          error: `Не удалось обновить translation ${translation?.documentId || translation?.id}`,
          updated,
          scanned: translations.length,
        };
      }
    }

    return {
      ok: true,
      dryRun,
      updated,
      scanned: translations.length,
      signsMapped: signIdToDocumentId.size,
      requestsMapped: requestIdToDocumentId.size,
    };
  },

  mergeSignRequestWithRequest: async ({ sourceRequest, targetRequest, lemma }) => {
    const sourceDocumentId = getRequestDocumentId(sourceRequest);
    const targetDocumentId = getRequestDocumentId(targetRequest);

    if (!sourceDocumentId || !targetDocumentId) {
      set({ signRequestsMutationError: "Не найден documentId заявки для слияния" });
      return { ok: false, updatedTranslations: 0 };
    }

    if (String(sourceDocumentId) === String(targetDocumentId)) {
      set({ signRequestsMutationError: "Нельзя объединить заявку саму с собой" });
      return { ok: false, updatedTranslations: 0 };
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({ signRequestsMutationError: "Нет токена" });
      return { ok: false, updatedTranslations: 0 };
    }

    const fromTokens = buildRequestSearchTokens(sourceRequest);
    const toToken = buildRequestPlaceholder(targetRequest);
    const safeLemma = String(lemma || "").trim();

    if (!fromTokens.length || !toToken) {
      set({ signRequestsMutationError: "Некорректный placeholder заявки" });
      return { ok: false, updatedTranslations: 0 };
    }

    set({ signRequestsMutationLoading: true, signRequestsMutationError: "" });

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const fetchJson = async (endpoint, init) => {
      const resp = await fetch(`${base}${endpoint}`, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
      if (!resp.ok) {
        let message = "Ошибка запроса";
        try {
          const errData = await resp.json();
          message = errData?.error?.message || errData?.message || message;
        } catch {
          const text = await resp.text();
          if (text) message = text;
        }
        throw new Error(message);
      }
      if (resp.status === 204) return null;
      return resp.json();
    };

    const fetchAllTranslations = () => fetchAllTranslationsFromApi(base, token);

    const updateTranslationSigns = async (translation, signs) => {
      const keys = Array.from(new Set([translation?.documentId, translation?.id].filter(Boolean)));
      const payload = {
        data: {
          data: {
            ...(translation?.data || {}),
            signs,
          },
        },
      };

      let lastError = null;
      for (const key of keys) {
        for (const endpoint of [`/api/translations/${key}`, `/translations/${key}`]) {
          try {
            await fetchJson(endpoint, { method: "PUT", body: JSON.stringify(payload) });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка обновления перевода");
    };

    const putSignRequest = async (request, data) => {
      const keys = Array.from(new Set([request?.documentId, request?.id].filter(Boolean)));
      let lastError = null;

      for (const key of keys) {
        for (const endpoint of [`/api/sign-requests/${key}`, `/sign-requests/${key}`]) {
          try {
            await fetchJson(endpoint, { method: "PUT", body: JSON.stringify({ data }) });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка обновления заявки");
    };

    const removeSignRequest = async (request) => {
      const keys = Array.from(new Set([request?.documentId, request?.id].filter(Boolean)));
      let lastError = null;

      for (const key of keys) {
        for (const endpoint of [`/api/sign-requests/${key}`, `/sign-requests/${key}`]) {
          try {
            await fetchJson(endpoint, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка удаления заявки");
    };

    try {
      const translations = await fetchAllTranslations();
      const affected = translations.filter((item) => translationContainsRequest(item, sourceRequest));
      let updatedTranslations = 0;

      for (const translation of affected) {
        const nextSigns = replaceSignTokenInList(translation.data.signs, fromTokens, toToken);
        await updateTranslationSigns(translation, nextSigns);
        updatedTranslations += 1;
      }

      const targetLemma = safeLemma || targetRequest?.lemma || "";
      if (targetLemma && targetLemma !== (targetRequest?.lemma || "")) {
        await putSignRequest(targetRequest, { lemma: targetLemma });
      }

      await removeSignRequest(sourceRequest);

      set((state) => ({
        signRequests: state.signRequests
          .filter((item) => String(getRequestDocumentId(item)) !== String(sourceDocumentId))
          .map((item) => {
            if (String(getRequestDocumentId(item)) !== String(targetDocumentId)) return item;
            return {
              ...item,
              lemma: targetLemma || item.lemma,
            };
          }),
        signRequestsMutationError: "",
      }));

      return { ok: true, updatedTranslations };
    } catch (error) {
      set({ signRequestsMutationError: error.message || "Ошибка слияния заявок" });
      return { ok: false, updatedTranslations: 0 };
    } finally {
      set({ signRequestsMutationLoading: false });
    }
  },

  mergeSignRequestWithSign: async ({ request, sign }) => {
    const requestDocumentId = getRequestDocumentId(request);
    const signDocumentId = buildSignDocumentEntry(sign);

    if (!requestDocumentId) {
      set({ signRequestsMutationError: "Не найден documentId заявки" });
      return { ok: false, updatedTranslations: 0 };
    }

    if (!signDocumentId) {
      set({ signRequestsMutationError: "Не найден documentId жеста" });
      return { ok: false, updatedTranslations: 0 };
    }

    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token;

    if (!token) {
      set({ signRequestsMutationError: "Нет токена" });
      return { ok: false, updatedTranslations: 0 };
    }

    const fromTokens = buildRequestSearchTokens(request);

    if (!fromTokens.length) {
      set({ signRequestsMutationError: "Некорректный placeholder заявки" });
      return { ok: false, updatedTranslations: 0 };
    }

    set({ signRequestsMutationLoading: true, signRequestsMutationError: "" });

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    const fetchJson = async (endpoint, init) => {
      const resp = await fetch(`${base}${endpoint}`, { ...init, headers: { ...headers, ...(init?.headers || {}) } });
      if (!resp.ok) {
        let message = "Ошибка запроса";
        try {
          const errData = await resp.json();
          message = errData?.error?.message || errData?.message || message;
        } catch {
          const text = await resp.text();
          if (text) message = text;
        }
        throw new Error(message);
      }
      if (resp.status === 204) return null;
      return resp.json();
    };

    const fetchAllTranslations = () => fetchAllTranslationsFromApi(base, token);

    const updateTranslationSigns = async (translation, signs) => {
      const keys = Array.from(new Set([translation?.documentId, translation?.id].filter(Boolean)));
      const payload = {
        data: {
          data: {
            ...(translation?.data || {}),
            signs,
          },
        },
      };

      let lastError = null;
      for (const key of keys) {
        for (const endpoint of [`/api/translations/${key}`, `/translations/${key}`]) {
          try {
            await fetchJson(endpoint, { method: "PUT", body: JSON.stringify(payload) });
            return true;
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка обновления перевода");
    };

    try {
      const translations = await fetchAllTranslations();
      const affected = translations.filter((item) => translationContainsRequest(item, request));
      let updatedTranslations = 0;

      for (const translation of affected) {
        const nextSigns = replaceSignTokenInList(translation.data.signs, fromTokens, signDocumentId);
        await updateTranslationSigns(translation, nextSigns);
        updatedTranslations += 1;
      }

      const keys = Array.from(new Set([request?.documentId, request?.id].filter(Boolean)));
      let lastError = null;

      for (const key of keys) {
        for (const endpoint of [`/api/sign-requests/${key}`, `/sign-requests/${key}`]) {
          try {
            await fetchJson(endpoint, {
              method: "PUT",
              body: JSON.stringify({ data: { added: true } }),
            });

            set((state) => ({
              signRequests: state.signRequests.map((item) => {
                if (String(getRequestDocumentId(item)) !== String(requestDocumentId)) return item;
                return { ...item, added: true };
              }),
              signRequestsMutationError: "",
            }));

            return { ok: true, updatedTranslations };
          } catch (error) {
            lastError = error;
          }
        }
      }

      throw lastError || new Error("Ошибка обновления статуса заявки");
    } catch (error) {
      set({ signRequestsMutationError: error.message || "Ошибка слияния заявки с жестом" });
      return { ok: false, updatedTranslations: 0 };
    } finally {
      set({ signRequestsMutationLoading: false });
    }
  },

  login: async (identifier, password) => {
    const base = (get().API || API_BASE).replace(/\/$/, "");
    set({ authLoading: true, authError: "" });

    try {
      const resp = await fetch(base + "/api/auth/local", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier, password }),
      });

      if (!resp.ok) {
        let errMessage = "Ошибка авторизации";
        try {
          const errData = await resp.json();
          errMessage = errData?.error?.message || errMessage;
        } catch {
          const errText = await resp.text();
          if (errText) errMessage = errText;
        }
        throw new Error(errMessage);
      }

      const data = await resp.json();
      if (!data?.jwt || !data?.user) {
        throw new Error("Неверный email или пароль");
      }

      let avatarUrl = DEFAULT_AVATAR;
      let avatarThumb = DEFAULT_AVATAR;

      try {
        const userExtraResp = await fetch(`${base}/api/users/${data.user.id}?populate=avatar`, {
          headers: {
            Authorization: `Bearer ${data.jwt}`,
          },
        });

        if (userExtraResp.ok) {
          const userExtra = await userExtraResp.json();
          const avatar = userExtra?.avatar;
          if (avatar?.url) avatarUrl = `${base}${avatar.url}`;
          if (avatar?.formats?.thumbnail?.url) {
            avatarThumb = `${base}${avatar.formats.thumbnail.url}`;
          } else {
            avatarThumb = avatarUrl;
          }
        }
      } catch {
        avatarUrl = DEFAULT_AVATAR;
        avatarThumb = DEFAULT_AVATAR;
      }

      const userWithToken = {
        ...data.user,
        token: data.jwt,
        avatarUrl,
        avatarThumb,
      };

      persistAuth(data.jwt, userWithToken);
      set({
        token: data.jwt,
        user: userWithToken,
        isAuthenticated: true,
        authError: "",
      });

      return userWithToken;
    } catch (error) {
      set({ authError: error.message || "Ошибка авторизации" });
      throw error;
    } finally {
      set({ authLoading: false });
    }
  },

  validateToken: async () => {
    const base = (get().API || API_BASE).replace(/\/$/, "");
    const token = get().token || localStorage.getItem("auth_token");

    if (!token) {
      set({ isAuthenticated: false, user: null });
      return false;
    }

    set({ authChecking: true });

    try {
      const resp = await fetch(base + "/api/users/me", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!resp.ok) {
        get().logout();
        return false;
      }

      const user = await resp.json();
      const previous = readJson("user_data") || {};
      const userWithToken = {
        ...previous,
        ...user,
        token,
      };

      persistAuth(token, userWithToken);
      set({ token, user: userWithToken, isAuthenticated: true, authError: "" });
      return true;
    } catch {
      get().logout();
      return false;
    } finally {
      set({ authChecking: false });
    }
  },
}));
